import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Download, DollarSign, AlertCircle } from "lucide-react";

const SpecialsTab = ({ data }) => {
  // Use new schema structure
  const billingSummary = data?.billing_summary || [];

  // Check if billing data is empty
  if (!data || billingSummary.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium" style={{ color: '#64748B' }}>No billing data available</p>
        <p className="text-sm mt-2" style={{ color: '#94a3b8' }}>
          No billing information was extracted from the documents.
        </p>
      </div>
    );
  }

  const handleDownloadCSV = () => {
    // Generate CSV for billing summary
    const headers = [
      'Facility Name',
      'Date(s) of Service',
      'Total Visits',
      'Total Billed'
    ];

    const rows = billingSummary.map(item => [
      item.facility_name,
      item.dates_of_service,
      item.total_visits || 0,
      item.total_billed || '$0.00'
    ]);

    // Add grand total row
    const grandTotal = billingSummary.reduce((sum, item) => {
      const amount = parseFloat(item.total_billed.replace(/[$,]/g, '')) || 0;
      return sum + amount;
    }, 0);
    
    const totalVisits = billingSummary.reduce((sum, item) => sum + (item.total_visits || 0), 0);
    
    rows.push([
      'GRAND TOTAL',
      '',
      totalVisits,
      `$${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-summary-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Billing summary downloaded as CSV");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle style={{ color: '#245451' }}>Medical Specials - Billing Summary</CardTitle>
            <CardDescription style={{ color: '#64748B' }}>
              Complete billing breakdown by facility
            </CardDescription>
          </div>
          <Button
            onClick={handleDownloadCSV}
            variant="outline"
            size="sm"
            disabled={billingSummary.length === 0}
            style={{ borderColor: '#245451', color: '#245451' }}
            data-testid="download-csv-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Download CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {billingSummary.length > 0 ? (
          <div className="space-y-6">
            {/* Billing Summary Table */}
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="billing-summary-table">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: 'rgba(36, 84, 81, 0.2)' }}>
                    <th className="text-left py-3 px-4 font-semibold" style={{ color: '#245451' }}>
                      Facility Name
                    </th>
                    <th className="text-left py-3 px-4 font-semibold" style={{ color: '#245451' }}>
                      Date(s) of Service
                    </th>
                    <th className="text-center py-3 px-4 font-semibold" style={{ color: '#245451' }}>
                      Total Visits
                    </th>
                    <th className="text-right py-3 px-4 font-semibold" style={{ color: '#245451' }}>
                      Total Billed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {billingSummary.map((bill, idx) => {
                    // Format the billed amount with commas
                    const formatBilledAmount = (value) => {
                      if (!value) return 'N/A';
                      const numValue = parseFloat(value.replace(/[$,]/g, '')) || 0;
                      return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    };
                    
                    return (
                      <tr key={idx} className="border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                        <td className="py-3 px-4 font-medium" style={{ color: '#245451' }}>
                          {bill.facility_name}
                        </td>
                        <td className="py-3 px-4" style={{ color: '#64748B' }}>
                          {bill.dates_of_service}
                        </td>
                        <td className="text-center py-3 px-4" style={{ color: '#64748B' }}>
                          {bill.total_visits}
                        </td>
                        <td className="text-right py-3 px-4 font-bold" style={{ color: '#C49603' }}>
                          {formatBilledAmount(bill.total_billed)}
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Grand Total Row */}
                  <tr className="border-t-2" style={{ borderColor: 'rgba(36, 84, 81, 0.3)' }}>
                    <td colSpan="2" className="py-4 px-4 font-bold text-right" style={{ color: '#245451' }}>
                      GRAND TOTAL MEDICAL SPECIALS:
                    </td>
                    <td className="text-center py-4 px-4 font-bold" style={{ color: '#245451' }}>
                      {billingSummary.reduce((sum, b) => sum + (b.total_visits || 0), 0)}
                    </td>
                    <td className="text-right py-4 px-4 font-bold text-xl" style={{ color: '#C49603' }} data-testid="grand-total">
                      {(() => {
                        const total = billingSummary.reduce((sum, b) => {
                          const amount = parseFloat(b.total_billed.replace(/[$,]/g, '')) || 0;
                          return sum + amount;
                        }, 0);
                        return `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Grand Total Card */}
            <div className="p-6 rounded-lg" style={{ backgroundColor: 'rgba(196, 150, 3, 0.1)', borderLeft: '4px solid #C49603' }}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(196, 150, 3, 0.15)' }}>
                  <DollarSign className="h-8 w-8" style={{ color: '#C49603' }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1" style={{ color: '#64748B' }}>
                    Total Medical Specials
                  </p>
                  <p className="text-4xl font-bold" style={{ color: '#C49603' }}>
                    {(() => {
                      const total = billingSummary.reduce((sum, b) => {
                        const amount = parseFloat(b.total_billed.replace(/[$,]/g, '')) || 0;
                        return sum + amount;
                      }, 0);
                      return `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    })()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium mb-1" style={{ color: '#64748B' }}>
                    Total Visits
                  </p>
                  <p className="text-3xl font-bold" style={{ color: '#245451' }}>
                    {billingSummary.reduce((sum, b) => sum + (b.total_visits || 0), 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <DollarSign className="h-16 w-16 mx-auto mb-4" style={{ color: '#84a2ac' }} />
            <p style={{ color: '#64748B' }}>No billing summary available</p>
            <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
              Upload billing documents and process the case to see the financial summary
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SpecialsTab;
